import FilterAlt from "@mui/icons-material/FilterAlt";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import type { AlertColor } from "@mui/material";
import { IconButton, Tooltip, useTheme } from "@mui/material";
import {
	getGridDateOperators,
	type GridRowId,
	type GridRowSelectionModel,
	useGridApiRef
} from "@mui/x-data-grid-pro";
import axios from "axios";
import {
	useCallback, useEffect, useMemo,
	useState
} from "react";

import apiRoutes from "../../constants/api-routes";
import messageLevels from "../../constants/message-levels";
import messages from "../../constants/messages";
import { handleDateFiltering } from "../../helpers/datagridFiltering";
import { emptyGridRowSelectionModel } from "../../helpers/dataGridRowSelectionModel";
import useAuthHeader from "../../hooks/useAuthHeader";
import useDatagridSorting from "../../hooks/useDatagridSorting";
import useDynamicContainerHeight from "../../hooks/useDynamicContainerHeight";
import useToastAlert from "../../hooks/useToastAlert";
import CoAppDataGridHeader from "../global/components/datagrid/CoAppDatagridHeader";
import DataGridCustomNoRowsOverlay from "../global/components/datagrid/DataGridCustomNoRowsOverlay";
import useFilteredDataRowCount from "../global/components/datagrid/useFilteredDataRowCount";
import useGridQuickFilterModel from "../global/components/datagrid/useGridQuickFilterModel";
import CoAppDestructiveConfirmationModal from "../global/components/modals/CoAppDestructiveConfirmationModal";
import UndoSnackbar from "../global/components/UndoSnackbar";
import {
	CoAppDataGrid, CoAppDataGridContainer
} from "../global/styled/global.styled";
import { getDifferenceInDays, getRuleTrashColumns, type RuleTrashGridRow } from "./columns/rule-trash-columns";
import RuleDataGridSelectionRow from "./RuleDataGridSelectionRow";

const datagridInitialState = {
	filter: {
		filterModel: {
			items: [],
			quickFilterExcludeHiddenColumns: true,
		},
	},
};

const dateColumns = ["dateTimeTrashed"];

export default function RuleTrashDataGrid() {
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("ruleTrash");
	// Extra 60px reserves space for the structural footer below the container.
	const { containerRef, containerHeight } = useDynamicContainerHeight(72);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarLevel, setSnackbarLevel] = useState<AlertColor | "">("");
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [isBulkUndo, setIsBulkUndo] = useState(false);
	const [rulesToUndo, setRulesToUndo] = useState<GridRowId[]>([]);
	const [ruleBeingRestored, setRuleBeingRestored] = useState<RuleTrashGridRow>({});
	const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(() => emptyGridRowSelectionModel());
	const [rules, setRules] = useState<RuleTrashGridRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [destructiveModalOpen, setDestructiveModalOpen] = useState(false);
	const [bulkDestructiveModalOpen, setBulkDestructiveModalOpen] = useState(false);
	const [ruleBeingDeleted, setRuleBeingDeleted] = useState<RuleTrashGridRow>({ name: "" });
	const { gridFilterModel, onFilterModelChange, quickFilterSearchProps } = useGridQuickFilterModel();
	const apiRef = useGridApiRef();
	const { visibleRuleCount } = useFilteredDataRowCount(apiRef, rules, loading, gridFilterModel);
	const [filterButtonEl, setFilterButtonEl] = useState<HTMLButtonElement | null>(null);
	const hasActiveFilter = (gridFilterModel.items?.length ?? 0) > 0;

	const handleRowSelectionChange = (newSelectionModel: GridRowSelectionModel) => {
		setSelectionModel(newSelectionModel);
	};

	const handleDeleteRule = () => {
		const ruleId = ruleBeingDeleted.id;
		if (ruleId === undefined || ruleId === null) return;
		axios.delete(apiRoutes.deleteRule + "/" + ruleId + "?isHardDelete=1", {
			headers: authHeader
		})
			.then(() => {
				handleDeleteRuleToggle();
				initTrashLibrary();
			})
			.catch(err => {
				console.error(err);
				handleToastAlert(messageLevels.ERROR, messages.PERM_DELETE_RULE_ERROR_MSG);
			});
	};

	const handleRestoreRule = useCallback((ruleId: GridRowId) => {
		axios.put(apiRoutes.restoreRule(ruleId), {}, {
			headers: authHeader
		})
			.then(() => {
				setSnackbarMessage(messages.RESTORE_RULE_MSG);
				setSnackbarLevel("info");
				setSnackbarOpen(true);
				setIsBulkUndo(false);
				setRuleBeingRestored((prev) => (
					prev.id === ruleId ? prev : { ...prev, id: ruleId }
				));
				initTrashLibrary();
			})
			.catch(err => {
				console.error(err);
				handleToastAlert(messageLevels.ERROR, messages.RULE_RESTORE_ERROR_MSG);
			});
	}, [authHeader, handleToastAlert]);

	const handleBulkRuleRestoration = () => {
		const restorationData = {
			ruleIds: [...selectionModel.ids]
		};

		axios.put(apiRoutes.bulkRestoreRules, restorationData, { headers: authHeader })
			.then(() => {
				setSnackbarMessage(messages.SEND_RULE_TO_TRASH_TOAST_MSG(selectionModel.ids.size > 1));
				setSnackbarLevel("info");
				setSnackbarOpen(true);
				setIsBulkUndo(true);
				setRulesToUndo([...selectionModel.ids]);
				initTrashLibrary();
			})
			.catch(err => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.BULK_RESTORE_FAILED_TOAST_MSG);
			});
	};

	const handleBulkRuleDeletion = () => {
		const deletionData = {
			ruleIds: [...selectionModel.ids]
		};

		axios.delete(apiRoutes.bulkPermanentDeleteRules + "?isHardDelete=1", { headers: authHeader, data: deletionData })
			.then(() => {
				initTrashLibrary();
				setBulkDestructiveModalOpen(false);
			})
			.catch(err => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.PERM_DELETE_RULE_ERROR_MSG);
			});
	};

	const handleUndoBulkRestoration = () => {
		const deleteData = {
			ruleIds: rulesToUndo
		};
		axios.delete(apiRoutes.bulkDeleteRules + "?isHardDelete=0", { headers: authHeader, data: deleteData })
			.then(() => {
				initTrashLibrary();
			})
			.catch(err => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.UNDO_FAILED_MSG("bulk restoration of rules"));
			});
	};

	const handleUndoRestoration = () => {
		const ruleId = ruleBeingRestored.id;
		if (ruleId === undefined || ruleId === null) return;
		axios.delete(apiRoutes.deleteRule + "/" + ruleId, { headers: authHeader })
			.then(() => {
				setRulesToUndo([...selectionModel.ids]);
				initTrashLibrary();
			})
			.catch(err => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.UNDO_FAILED_MSG("restoration of rule"));
			});
	};

	const handleDeleteRuleToggle = useCallback((rule: RuleTrashGridRow = { name: "" }) => {
		setDestructiveModalOpen(prev => !prev);
		setRuleBeingDeleted(rule);
	}, []);

	const theme = useTheme();
	const columns = useMemo(() => (
		getRuleTrashColumns(
			handleDeleteRuleToggle,
			handleRestoreRule,
			theme
		)
	), [theme, handleDeleteRuleToggle, handleRestoreRule]);

	const invisibleFields = ["id"];
	const filterableColumns = useMemo(
		() => columns
			.filter((column) => !invisibleFields.includes(column.field))
			.map((column) => {
				if (dateColumns.includes(column.field)) {
					return {
						...column,
						getApplyQuickFilterFn: handleDateFiltering,
						filterOperators: getGridDateOperators(true)
					};
				}
				if (column.field === "delete" || column.field === "restore") {
					return { ...column, filterable: false };
				}
				return column;
			})
			.map((c) => ((["name", "description"].includes(c.field) && c.flex == null)
				? { ...c, flex: 1, minWidth: c.minWidth ?? c.width ?? 200 }
				: c)),
		[columns]);

	const overlaySlotProps = {
		noResultsOverlay: { message: "No rules found." },
		noRowsOverlay: { message: "No rules found." }
	// Custom overlay reads `message` — DataGrid slot typings omit it.
	} as Record<string, unknown>;

	const filterPanelSlotProps = {
		...overlaySlotProps,
		loadingOverlay: {
			variant: "skeleton" as const,
			noRowsVariant: "skeleton" as const,
		},
		panel: {
			target: filterButtonEl ?? undefined
		},
		filterPanel: {
			filterFormProps: {
				logicOperatorInputProps: {
					size: "small",
				},
				columnInputProps: {
					size: "small",
				},
				operatorInputProps: {
					size: "small",
				},
				valueInputProps: {
					InputComponentProps: {
						size: "small",
					},
				},
			},
			sx: {
				"& .MuiDataGrid-filterFormLogicOperatorInput": { mr: 2 },
				"& .MuiDataGrid-filterFormColumnInput": { mr: 2, width: 150 },
				"& .MuiDataGrid-filterFormOperatorInput": { mr: 2 },
				"& .MuiDataGrid-filterFormValueInput": { width: 200 }
			},
		},
	};

	const initTrashLibrary = () => {
		setLoading(true);
		setRules([]);
		setSelectionModel(emptyGridRowSelectionModel());
		axios.get(apiRoutes.getRules + "?isTrashed=1", { headers: authHeader })
			.then(res => {
				const loaded = (res.data.rules ?? []) as RuleTrashGridRow[];
				loaded.forEach(rule => {
					const trashed = rule.dateTimeTrashed;
					rule.permanentDeletionIn = trashed ? getDifferenceInDays(new Date(trashed)) : undefined;
				});
				setRules(loaded);
			})
			.catch(err => console.log(err))
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		initTrashLibrary();
		handleInitializeSortModel();
	}, []);

	return (
		<>
			<CoAppDataGridHeader
				iconKind="rules"
				title="Trash"
				resourceType="Rule"
				addIsPresent={false}
				editIsPresent={false}
				deleteIsPresent={false}
				searchIsPresent={true}
				{...quickFilterSearchProps}
				searchPlaceholder="Search all columns…"
				filterControl={
					<Tooltip title={hasActiveFilter ? "Filters applied — edit" : "Filter"}>
						<IconButton
							ref={setFilterButtonEl}
							aria-label={hasActiveFilter ? "Edit filters (filters applied)" : "Filter"}
							onClick={() => apiRef.current?.showFilterPanel()}
							size="small"
							sx={{
								borderRadius: "8px",
								border: hasActiveFilter ? "1px solid rgba(2, 213, 171, 0.40)" : "1px solid rgba(13, 31, 48, 0.12)",
								background: hasActiveFilter ? "rgba(2, 213, 171, 0.12)" : "#FFFFFF",
								color: hasActiveFilter ? "#02B894" : "rgba(13, 31, 48, 0.55)",
								transition: "background 0.15s, border-color 0.15s, color 0.15s",
								"&:hover": { background: "rgba(2, 213, 171, 0.10)", borderColor: "rgba(2, 213, 171, 0.35)", color: "#02B894" },
							}}
						>
							{hasActiveFilter ? <FilterAlt sx={{ fontSize: 18 }} /> : <FilterAltOutlined sx={{ fontSize: 18 }} />}
						</IconButton>
					</Tooltip>
				}
			/>
		<CoAppDataGridContainer ref={containerRef} sx={{ height: containerHeight }}>
				<CoAppDataGrid
					apiRef={apiRef}
					sx={{}}
					loading={loading}
					rows={rules}
					columns={filterableColumns}
					checkboxSelection
					checkboxSelectionVisibleOnly
					disableRowSelectionOnClick
					// Theme sets disableColumnPinning: true globally — opt in so pinned
					// restore/delete columns stay fixed while the rest scroll horizontally.
					disableColumnPinning={false}
					initialState={{
						...datagridInitialState,
						pinnedColumns: { right: ["restore", "delete"] }
					}}
					rowSelectionModel={selectionModel}
					onRowSelectionModelChange={handleRowSelectionChange}
					filterModel={gridFilterModel}
					onFilterModelChange={onFilterModelChange}
					slots={{
						noResultsOverlay: DataGridCustomNoRowsOverlay,
						noRowsOverlay: DataGridCustomNoRowsOverlay
					}}
					slotProps={filterPanelSlotProps}
					hideFooter
					sortModel={sortModel}
					onSortModelChange={handleSortModelChange}
				/>
			</CoAppDataGridContainer>
			<RuleDataGridSelectionRow
				selectedRules={Array.from(selectionModel.ids)}
				visibleRuleCount={visibleRuleCount}
				loading={loading}
				isTrash={true}
				onClearSelectionClickHandler={() => setSelectionModel(emptyGridRowSelectionModel())}
				onRestoreClickHandler={handleBulkRuleRestoration}
				onDeleteClickHandler={() => setBulkDestructiveModalOpen(true)}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={destructiveModalOpen}
				dialogTitle={`Permanently delete ${ruleBeingDeleted.name ?? ""} rule?`}
				dialogMessage={messages.DELETION_CONFIRMATION_MSG(ruleBeingDeleted.name ?? "", "rule")}
				confirmClickHandler={handleDeleteRule}
				cancelClickHandler={() => handleDeleteRuleToggle()}
				actionText="Permanently delete"
			/>

			<CoAppDestructiveConfirmationModal
				dialogOpen={bulkDestructiveModalOpen}
				dialogTitle="Permanently delete selected rules?"
				dialogMessage={messages.BULK_PERMANENTLY_DELETE_MSG("rules")}
				confirmClickHandler={handleBulkRuleDeletion}
				cancelClickHandler={() => setBulkDestructiveModalOpen(false)}
				actionText="Permanently delete"
			/>
			<UndoSnackbar
				level={snackbarLevel}
				message={snackbarMessage}
				open={snackbarOpen}
				setLevel={setSnackbarLevel}
				setMessage={setSnackbarMessage}
				setOpen={setSnackbarOpen}
				undoHandler={isBulkUndo ? handleUndoBulkRestoration : handleUndoRestoration}
			/>
		</>
	);
}
